<template>
    <!-- Conversation Modal -->
    <ConversationViewModal
        v-if="modals.conversation_view.is_open"
        :open="modals.conversation_view.is_open"
        :conversation="modals.conversation_view.conversation"
        @close="$store.commit('modal:close', 'conversation_view')"
        @reload="fetchConversations"
        @reply="handleReply"
    />

    <!-- Compose Modal -->
    <EmailComposeModal
        v-if="modals.email_compose.is_open"
        :open="modals.email_compose.is_open"
        :reply-to-message="modals.email_compose.replyToMessage || null"
        :reply-to-conversation="modals.email_compose.replyToConversation || null"
        :prefilled-recipient="modals.email_compose.recipient || ''"
        :prefilled-recipients="modals.email_compose.recipients || []"
        :prefilled-subject="modals.email_compose.subject || ''"
        :creator-mode="modals.email_compose.creatorMode"
        @close="$store.commit('modal:close', 'email_compose')"
        @sent="fetchConversations"
    />

    <div class="mt-6">
        <div class="mb-5 mt-8 flex items-center justify-between">
            <div>
                <h2 class="font-semibold text-slate-800">Messages</h2>
                <p class="text-sm text-slate-600">A list of messages associated with this creator.</p>
            </div>

            <Button v-if="creatorEmail" @click="openComposeModal">
                <AppIcon name="envelope" class="mr-2 h-4 w-4" />
                New Email
            </Button>
        </div>

        <div v-if="loading" class="text-sm italic text-slate-500">Loading messages...</div>
        <div v-else-if="error" class="flex items-center gap-3 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3">
            <AppIcon name="circle-exclamation" class="h-5 w-5 shrink-0 text-rose-400" />
            <p class="text-sm text-rose-700">{{ error }}</p>
        </div>

        <!-- Conversations Table -->
        <div v-else class="max-h-[400px] overflow-y-auto rounded-lg border border-slate-200 shadow-sm">
            <table class="min-w-full divide-y divide-slate-200">
                <thead class="bg-slate-50">
                    <tr>
                        <th class="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-slate-500">
                            Subject
                        </th>
                        <th class="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-slate-500">
                            Status
                        </th>
                        <th class="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-slate-500">
                            Last Message
                        </th>
                        <th class="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-slate-500">
                            Messages
                        </th>
                    </tr>
                </thead>

                <tbody class="divide-y divide-slate-200 bg-white">
                    <tr
                        v-for="conv in conversations"
                        :key="conv.id"
                        class="group cursor-pointer hover:bg-slate-50"
                        @click="openConversationModal(conv)"
                    >
                        <!-- Subject + Hover 'Open' pill (matches EmailConversations.vue) -->
                        <td class="max-w-[16rem] truncate px-4 py-2 text-sm font-medium text-slate-800">
                            <div class="relative flex items-center">
                                <div class="max-w-xs">
                                    <div class="font-medium text-slate-900">
                                        {{ conv.subject || '(No Subject)' }}
                                    </div>
                                </div>

                                <div
                                    class="absolute right-0 top-0 hidden h-full cursor-pointer rounded bg-slate-100 px-2 text-xs font-semibold transition duration-200 group-hover:flex hover:bg-slate-200"
                                    @click.stop="openConversationModal(conv)"
                                >
                                    <span class="my-auto flex uppercase">
                                        <AppIcon
                                            name="rectangle-list"
                                            class="my-auto mr-1 h-4 w-4"
                                        />
                                        <span class="my-auto leading-none">Open</span>
                                    </span>
                                </div>
                            </div>
                        </td>

                        <td class="px-4 py-2 text-sm">
                            <span
                                class="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium"
                                :class="{
                                    'bg-green-100 text-green-800': conv.status === 'active',
                                    'bg-gray-100 text-gray-800': conv.status === 'archived',
                                    'bg-yellow-100 text-yellow-800': conv.status === 'snoozed',
                                }"
                            >
                                {{ conv.status }}
                            </span>
                        </td>

                        <td class="px-4 py-2 text-sm text-slate-700">
                            {{ formatDate(conv.last_message_at) }}
                        </td>

                        <td class="px-4 py-2 text-sm">
                            <Badge variant="secondary">{{ conv.message_count }}</Badge>
                        </td>
                    </tr>

                    <tr v-if="!conversations.length">
                        <td colspan="4" class="px-4 py-6 text-center text-sm text-slate-500">
                            No messages found.
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import { useStore, mapGetters } from 'vuex'
import emailAPI from '@/utils/api/api.email'
import moment from 'moment'
import { axios } from '@/utils/axios'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import EmailComposeModal from '@/partials/modals/EmailComposeModal.vue'
import ConversationViewModal from '@/partials/modals/ConversationViewModal.vue'

export default {
    name: 'CreatorConversations',

    components: {
        Badge,
        Button,
        EmailComposeModal,
        ConversationViewModal,
    },

    props: {
        creatorEmail: {
            type: String,
            required: true,
        },
    },

    computed: { ...mapGetters(['modals']) },

    setup(props) {
        const store = useStore()
        const conversations = ref([])
        const loading = ref(true)
        const error = ref(null)

        async function fetchConversations() {
            try {
                const { success, value } = await emailAPI.getConversationsByCreator(axios, props.creatorEmail)
                if (success) {
                    conversations.value = value.conversations || []
                } else {
                    error.value = 'Failed to load messages.'
                }
            } catch (err) {
                error.value = 'Error fetching messages.'
            } finally {
                loading.value = false
            }
        }

        function formatDate(date) {
            return date ? moment(date).fromNow() : '—'
        }

        function openConversationModal(conversation) {
            store.commit('modal:setData', {
                modal_id: 'conversation_view',
                data: {
                    is_open: true,
                    conversation,
                },
            })
        }

        async function handleReply(replyData) {
            store.commit('modal:close', 'conversation_view')

            const composeData = {
                is_open: true,
                recipient: null,
                subject: null,
                replyToMessage: null,
                replyToConversation: null,
                creatorMode: false,
            }

            if (replyData.type === 'message') {
                composeData.replyToMessage = replyData.message
                composeData.replyToConversation = replyData.conversation
                composeData.subject = replyData.conversation?.subject ? `Re: ${replyData.conversation.subject}` : 'Re:'
                composeData.recipient = replyData.message?.from_email || null
            } else if (replyData.type === 'conversation') {
                composeData.replyToConversation = replyData.conversation
                composeData.subject = replyData.conversation?.subject ? `Re: ${replyData.conversation.subject}` : 'Re:'
                composeData.recipient = replyData.conversation?.creator_email || null
            }

            store.commit('modal:open', 'email_compose')

            store.commit('modal:setData', {
                modal_id: 'email_compose',
                data: composeData,
            })
        }

        function openComposeModal() {
            store.commit('modal:setData', {
                modal_id: 'email_compose',
                data: {
                    is_open: true,
                    recipient: props.creatorEmail,
                    subject: null,
                    replyToMessage: null,
                    replyToConversation: null,
                    creatorMode: true,
                },
            })
            store.commit('modal:open', 'email_compose')
        }

        onMounted(fetchConversations)

        return {
            conversations,
            loading,
            error,
            formatDate,
            fetchConversations,
            openConversationModal,
            handleReply,
            openComposeModal,
        }
    },
}
</script>
